import { useRef, useState } from "react";
import PropTypes from "prop-types";
import { InputTextarea } from "primereact/inputtextarea";
import Toast from "@moltech/molui/toast";
import {
  getStartersForModule,
  hasUnresolvedEntityToken,
} from "./copilotUtils";
import useCopilotComposerSnippets from "./useCopilotComposerSnippets";

export const copyCopilotText = async (text) => {
  const value = String(text || "").trim();
  if (!value) {
    Toast.warn("Nothing to copy.");
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    Toast.success("Copied to clipboard. Nothing was sent.");
  } catch {
    Toast.error("Unable to copy to clipboard.");
  }
};

const CopilotComposer = ({
  input,
  setInput,
  isLoading,
  module,
  jobOpeningId,
  activeWorkflow,
  attachments,
  onSend,
  onAttachFiles,
  onDraftPrompt,
  isWelcomeScreen = false,
}) => {
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const [startersOpen, setStartersOpen] = useState(true);
  const starters = getStartersForModule(module, jobOpeningId);
  const { snippetPanel, snippetOpen } = useCopilotComposerSnippets(input, setInput);
  const awaitingEntityPick = hasUnresolvedEntityToken(input);

  return (
    <div className={`copilot-composer-container ${isWelcomeScreen ? "is-welcome" : ""}`}>
      {/* Attachments List */}
      {attachments?.length ? (
        <div className="copilot-composer__attachments">
          {attachments.map((a) => (
            <span key={a.applicantResumeId || a.fileName} className="copilot-attachment-chip">
              <i className="pi pi-paperclip" />
              {a.fileName || `Resume #${a.applicantResumeId}`}
            </span>
          ))}
        </div>
      ) : null}

      {/* Collapsible Suggestions starters list (hide if on welcome screen) */}
      {!isWelcomeScreen && starters.length > 0 && (
        <div className="copilot-composer__starters-wrap">
          <button
            type="button"
            className="copilot-composer__starters-toggle"
            onClick={() => setStartersOpen((v) => !v)}
          >
            <i className={`pi ${startersOpen ? "pi-chevron-down" : "pi-chevron-right"}`} />
            <span>Suggestions</span>
          </button>
          {startersOpen ? (
            <div className="copilot-composer__starters">
              {starters.slice(0, 6).map((s) => (
                <button
                  key={s}
                  type="button"
                  className="copilot-starter-pill"
                  disabled={isLoading}
                  onClick={() => {
                    onDraftPrompt?.(s);
                    requestAnimationFrame(() =>
                      boxRef.current?.querySelector("textarea")?.focus()
                    );
                  }}
                  title={s}
                >
                  {s.length > 36 ? `${s.slice(0, 36)}…` : s}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Redesigned Composer Card Box (Horizontal bar matching Image 1) */}
      <div className="copilot-composer-box" ref={boxRef}>
        {snippetPanel}

        {/* Hidden file inputs */}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length) onAttachFiles?.(files);
            e.target.value = "";
          }}
        />

        {/* Attach file button */}
        <button
          type="button"
          className="copilot-composer-attach-btn"
          disabled={isLoading}
          onClick={() => fileRef.current?.click()}
          title="Attach resume"
        >
          <i className="pi pi-paperclip" />
        </button>

        {/* Text Input Area */}
        <InputTextarea
          value={input}
          rows={1}
          autoResize
          className="copilot-composer-textarea"
          placeholder={
            activeWorkflow
              ? "Answer the step above, or type cancel…"
              : "Ask MolAssist anything…"
          }
          disabled={isLoading}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (snippetOpen || awaitingEntityPick) return;
              onSend(input);
            }
          }}
        />

        {/* Send Button */}
        <button
          type="button"
          className="copilot-composer-send-btn"
          disabled={isLoading || !input.trim() || awaitingEntityPick}
          onClick={() => onSend(input)}
        />
      </div>
    </div>
  );
};

CopilotComposer.propTypes = {
  input: PropTypes.string.isRequired,
  setInput: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  module: PropTypes.string,
  jobOpeningId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  activeWorkflow: PropTypes.object,
  attachments: PropTypes.array,
  onSend: PropTypes.func.isRequired,
  onAttachFiles: PropTypes.func,
  onDraftPrompt: PropTypes.func,
  isWelcomeScreen: PropTypes.bool,
};

export default CopilotComposer;
